import React, { useState } from 'react';

import Button from 'components/button/Button';
import { useRouteContext } from 'context/RouterContext';
import { useWelcomeCoupon } from 'entities/coupon';
import { Copy } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { WELCOME_PATH } from 'shared/constants/route';
import { KOREA_LIFE_GUIDE_URL } from 'shared/constants/url';
import { useLangNavigate } from 'shared/hooks';
import { getPathWithoutLang } from 'shared/lib/converter';
import { toast } from 'sonner';

interface SignupStep5Props {
  onClose: () => void;
  isProfileCompletion?: boolean;
  isInline?: boolean;
}

const SignupStep5 = ({ onClose, isProfileCompletion = false, isInline = false }: SignupStep5Props) => {
  const { t } = useTranslation('auth');
  const { coupon } = useWelcomeCoupon();
  const [isCopied, setIsCopied] = useState(false);
  const { navigateWithLang } = useLangNavigate();
  const location = useLocation();
  const { redirectPath, setRedirectPath } = useRouteContext();
  // 웰컴 랜딩에서 가입한 경우: 완료 화면 안에서 쿠폰 복사·가이드 연결까지 바로 제공
  // (welcome ns 키는 랜딩 페이지가 이미 로드한 상태에서만 이 분기가 렌더되므로 안전)
  const isWelcomeLanding = getPathWithoutLang(location.pathname) === WELCOME_PATH;
  const profileCompletionWidth = isInline ? 'w-full' : 'w-[calc(100vw-24px)]';

  // 가입은 보던 페이지 위의 모달이라, 닫으면 원래 보던 매물/페이지로 돌아간다.
  // 로그인이 필요한 페이지(장바구니 등)로 가려다 가입한 경우엔 그 페이지로 보낸다
  const handleContinue = () => {
    window.dataLayer?.push({
      event: 'sign_up_step5_cta',
      cta: 'continue',
    });
    onClose();
    if (isInline) return;
    const urlRedirectPath = new URLSearchParams(location.search).get('redirect');
    if (urlRedirectPath) {
      navigateWithLang(getPathWithoutLang(urlRedirectPath));
    } else if (redirectPath) {
      navigateWithLang(redirectPath);
    }
    setRedirectPath(null);
  };

  const handleGoCheckBookingGuide = () => {
    window.dataLayer?.push({
      event: 'sign_up_step5_cta',
      cta: 'guide',
    });
    onClose();
    navigateWithLang('/guide');
  };

  const handleCopyCoupon = async () => {
    if (!coupon) return;
    setIsCopied(false);
    try {
      await navigator.clipboard.writeText(coupon.code);
      setIsCopied(true);
      window.dataLayer?.push({ event: 'welcome_coupon_copy', section: 'signup_complete' });
    } catch (error) {
      // 인앱브라우저 등 clipboard API 미지원 환경 — 코드가 화면에 있으므로 수동 복사 안내
      toast.error(t('onboarding.copyError'));
      console.error(error);
    }
  };

  const handleOpenGuide = () => {
    window.dataLayer?.push({ event: 'sign_up_step5_cta' });
    window.dataLayer?.push({ event: 'welcome_guide_click', section: 'signup_complete' });
    window.open(KOREA_LIFE_GUIDE_URL, '_blank');
  };

  const getDescriptionKey = () => {
    if (isProfileCompletion)
      return coupon ? 'auth:onboarding.completedDescription' : 'auth:onboarding.completedWithoutCoupon';
    if (isWelcomeLanding && coupon) return 'auth:signup.step5.benefitsDescription';
    return 'auth:signup.step5.description';
  };

  return (
    <div
      className={
        isProfileCompletion
          ? `flex ${profileCompletionWidth} max-w-[440px] flex-col items-center gap-6 px-6 pb-7 pt-7`
          : 'flex w-[309px] max-w-full flex-col items-center justify-center gap-y-5 px-5 py-6 md:mx-45 md:my-10 md:gap-y-6.25 md:p-0'
      }
    >
      <div className="flex w-full flex-col items-center gap-y-1.5">
        <img src="/images/optimized/illusts/il_check.webp" alt="signup" className="h-[54px] w-[54px] object-cover" />

        <div className="flex w-full flex-col items-center gap-y-2">
          <h1 className="text-center text-t4">
            {t(isProfileCompletion ? 'onboarding.completedTitle' : 'signup.step5.welcome')}
          </h1>
          <span className="w-full whitespace-normal break-words text-center text-c2 text-gray-40">
            <Trans
              i18nKey={getDescriptionKey()}
              components={{
                br: <br />,
                b: <b className="font-semibold text-[#222]" />,
                mobile: <span className="block min-[800px]:inline" />,
              }}
            />
          </span>
        </div>
      </div>

      {isProfileCompletion && (
        <div className="flex w-full flex-col gap-y-3">
          {coupon && (
            <div className="overflow-hidden rounded-xl border border-primary/20 bg-primaryLight">
              <div className="border-b border-dashed border-primary/30 px-4 py-3 text-left">
                <p className="text-[14px] font-semibold leading-5 text-primary">{t('onboarding.rewardTitle')}</p>
                <p className="mt-1 text-[12px] font-normal leading-[18px] text-gray-50">
                  {t('onboarding.couponUsage')}
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
                <span className="text-c1 font-bold text-[#222]">{coupon.code}</span>
                <button
                  type="button"
                  onClick={handleCopyCoupon}
                  className="min-h-11 text-c1 font-semibold text-primary"
                >
                  <span aria-live="polite">{t(isCopied ? 'onboarding.copied' : 'onboarding.copyCoupon')}</span>
                </button>
              </div>
            </div>
          )}
          {coupon && (
            <p className="whitespace-pre-line text-center text-c2 text-gray-50">{t('onboarding.couponNote')}</p>
          )}
          <Button
            text={t(isInline ? 'onboarding.manageProfile' : 'signup.step5.continue')}
            shape="SQUARE"
            onClick={handleContinue}
          />
        </div>
      )}
      {!isProfileCompletion && isWelcomeLanding && (
        <div className="flex w-full flex-col gap-y-3">
          {coupon && (
            <div className="flex items-center justify-between gap-x-2 rounded-lg border border-dashed border-primary bg-primaryLight px-3 py-3">
              <span className="whitespace-nowrap text-c1 font-bold text-[#222]">{coupon.code}</span>
              <button
                type="button"
                onClick={handleCopyCoupon}
                className="flex shrink-0 items-center gap-x-1 text-c1 font-semibold text-primary"
              >
                <Copy className="h-4 w-4" />
                <span aria-live="polite">{t(isCopied ? 'onboarding.copied' : 'welcome:benefits.coupon.copy')}</span>
              </button>
            </div>
          )}
          <Button text={t('welcome:benefits.guide.cta')} shape="SQUARE" onClick={handleOpenGuide} />
        </div>
      )}
      {!isProfileCompletion && !isWelcomeLanding && (
        <div className="flex w-full flex-col items-center gap-y-3">
          <Button text={t('signup.step5.continue')} shape="SQUARE" onClick={handleContinue} />
          <button
            type="button"
            onClick={handleGoCheckBookingGuide}
            className="text-c2 text-gray-40 underline underline-offset-2 hover:text-black"
          >
            {t('signup.step5.goCheckBookingGuide')}
          </button>
        </div>
      )}
    </div>
  );
};

export default SignupStep5;
